/**
 * "Tests" gadget (Verify): the last run's tests per service (passed or failed, the line coverage) from the Quality
 * overview (shared with Dependencies and Security), and a bar per recent run that ran tests (`GET /quality/runs`:
 * red when a test failed). Before any run: says so. The runs list failing alone: the last run is still shown.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ago } from '../../../utils/blameModel';
import { testSummary, testTrend } from '../../../utils/homeGadgets/verify';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useQualityOverview, useQualityRuns } from './sources';

const isoOf = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString() : null);

export default function TestsGadget({ projectId, onOpenQuality = null }) {
  const overview = useQualityOverview(projectId);
  const runs = useQualityRuns(projectId);
  const summary = testSummary(overview.data);
  const trend = testTrend(runs.data);
  const totals = summary.totals;
  return (
    <GadgetCard links={<TabLink onClick={onOpenQuality}>Verify › Quality</TabLink>} meta={summary.ranAt ? ago(isoOf(summary.ranAt)) : null} testId="tests-gadget" title="Tests">
      <GadgetBody
        data={overview.data}
        empty={!summary.ran}
        emptyText="No test has run yet. Run the checks in Quality."
        error={overview.error}
        errorText="The tests could not be read just now."
        loading={overview.loading}
        loadingText="Reading the tests…"
      >
        {() => (
          <>
            {totals && (
              <p className="gd-note" data-testid="tests-totals">
                <span className={totals.failed > 0 ? 'gd-bad' : 'gd-ok'}>{totals.failed > 0 ? `${totals.failed} failed` : 'All passed'}</span>
                {` of ${totals.total} ${totals.total === 1 ? 'test' : 'tests'}`}
              </p>
            )}
            <ul className="gd-rows">
              {summary.services.map((s) => (
                <li className="gd-row" data-testid={`tests-${s.service}`} key={s.service}>
                  <strong>{s.service}</strong>
                  <span className="gd-grow ph-muted">
                    {s.total > 0 ? <span className={s.failed > 0 ? 'gd-bad' : ''}>{s.failed > 0 ? `${s.failed} of ${s.total} failed` : `${s.total} passed`}</span> : (s.message || 'No tests')}
                  </span>
                  <span className="gd-num ph-muted">{s.coverage === null ? 'no coverage' : `${s.coverage}% covered`}</span>
                </li>
              ))}
            </ul>
            {trend.length > 1 && (
              <div aria-label={`Last ${trend.length} runs: ${trend.filter((r) => !r.ok).length} with failures`} className="gd-trend" data-testid="tests-trend" role="img">
                {trend.map((r) => <span className={r.ok ? '' : 'is-bad'} key={r.runId} style={{ height: `${Math.max(20, Math.round(((r.total - r.failed) / Math.max(1, r.total)) * 100))}%` }} title={`${r.failed} of ${r.total} failed`} />)}
              </div>
            )}
            {runs.error && !runs.data && <p className="gd-note">The earlier runs could not be read just now: only the last one is shown.</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

TestsGadget.propTypes = { onOpenQuality: PropTypes.func, projectId: PropTypes.string.isRequired };
